import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Heading, 
  Text, 
  Card, 
  Container, 
  Checkbox, 
  Textarea,
  Alert,
  Link,
  StatusDot,
  ThemeSwitcher
} from 'atomiccomponents-react-ui';
import 'atomiccomponents-react-ui/tokens.css';
import './index.css';

// Supported languages for speech recognition
const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
  { code: 'es-ES', name: 'Spanish', flag: '🇪🇸' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', flag: '🇹🇼' },
  { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ar-SA', name: 'Arabic', flag: '🇸🇦' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', flag: '🇵🇹' },
  { code: 'bn-IN', name: 'Bengali', flag: '🇧🇩' },
  { code: 'ru-RU', name: 'Russian', flag: '🇷🇺' },
  { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
  { code: 'de-DE', name: 'German', flag: '🇩🇪' },
  { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
  { code: 'tr-TR', name: 'Turkish', flag: '🇹🇷' },
  { code: 'ko-KR', name: 'Korean', flag: '🇰🇷' },
  { code: 'it-IT', name: 'Italian', flag: '🇮🇹' },
  { code: 'uk-UA', name: 'Ukrainian', flag: '🇺🇦' },
  { code: 'pl-PL', name: 'Polish', flag: '🇵🇱' },
  { code: 'nl-NL', name: 'Dutch', flag: '🇳🇱' },
];

function App() {
  console.log('App component rendering...');
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [status, setStatus] = useState('Ready to record');
  const [appendMode, setAppendMode] = useState(true);
  const [currentSpeaker, setCurrentSpeaker] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'en-US';
  });
  const [clipboardHistory, setClipboardHistory] = useState(() => {
    const savedHistory = localStorage.getItem('clipboardHistory');
    if (savedHistory) {
      try {
        return JSON.parse(savedHistory);
      } catch (error) {
        console.error('Error loading clipboard history:', error);
        return [];
      }
    }
    return [];
  });
  const [showHistory, setShowHistory] = useState(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const historyRef = useRef(null);

  console.log('Current state:', { isRecording, status, transcript: transcript.length });

  // Clipboard history functions
  const addToClipboardHistory = (text) => {
    if (!text || text.trim() === '') return;
    
    const newItem = {
      id: Date.now(),
      text: text.trim(),
      timestamp: new Date().toISOString(),
      preview: text.trim().substring(0, 50) + (text.trim().length > 50 ? '...' : '')
    };

    setClipboardHistory(prevHistory => {
      // Remove duplicate if exists (same text)
      const filteredHistory = prevHistory.filter(item => item.text !== newItem.text);
      
      // Add new item to the beginning and keep only last 30 items
      const updatedHistory = [newItem, ...filteredHistory].slice(0, 30);
      
      // Save to localStorage
      localStorage.setItem('clipboardHistory', JSON.stringify(updatedHistory));
      
      return updatedHistory;
    });
  };

  const copyFromHistory = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus('Text copied from history');
      
      // Google Analytics event
      if (typeof gtag !== 'undefined') {
        gtag('event', 'copy_from_history', {
          event_category: 'History',
          event_label: 'Copy individual item'
        });
      }
    } catch (error) {
      console.error('Failed to copy from history:', error);
      setStatus('Failed to copy text from history');
    }
  };

  const copyAllHistory = async () => {
    if (clipboardHistory.length === 0) {
      setStatus('No history items to copy');
      return;
    }

    try {
      // Join all history text items with newlines (oldest first)
      const allText = clipboardHistory.slice().reverse().map(item => item.text).join('\n');
      await navigator.clipboard.writeText(allText);
      addToClipboardHistory(allText); // Add the combined text to history
      setStatus(`Copied all ${clipboardHistory.length} history items (oldest first)`);
      
      // Google Analytics event
      if (typeof gtag !== 'undefined') {
        gtag('event', 'copy_all_history', {
          event_category: 'History',
          event_label: 'Copy all items',
          value: clipboardHistory.length
        });
      }
    } catch (error) {
      console.error('Failed to copy all history:', error);
      setStatus('Failed to copy all history items');
    }
  };

  const clearHistory = () => {
    setClipboardHistory([]);
    localStorage.removeItem('clipboardHistory');
    setStatus('Clipboard history cleared');
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'clear_history', {
        event_category: 'History',
        event_label: 'Clear all history'
      });
    }
  };

  const deleteHistoryItem = (id) => {
    setClipboardHistory(prevHistory => {
      const updatedHistory = prevHistory.filter(item => item.id !== id);
      localStorage.setItem('clipboardHistory', JSON.stringify(updatedHistory));
      return updatedHistory;
    });
    setStatus('History item deleted');
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'delete_history_item', {
        event_category: 'History',
        event_label: 'Delete individual item'
      });
    }
  };

  // Initialize speech recognition on component mount
  useEffect(() => {
    console.log('Initializing speech recognition...');
    
    // Check if speech recognition is supported
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.log('Speech recognition not supported');
      return;
    }

    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = selectedLanguage;

    recognitionRef.current = recognition;
    
    console.log('Speech recognition initialized successfully with language:', selectedLanguage);
  }, [selectedLanguage]);

  // Simplified start/stop functions for testing
  const startRecording = () => {
    console.log('Start recording clicked');
    
    if (!recognitionRef.current) {
      setStatus('Speech recognition not initialized');
      return;
    }
    
    if (isRecording) {
      setStatus('Already recording...');
      return;
    }

    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'start_recording', {
        event_category: 'Speech Recognition',
        event_label: 'Start recording button'
      });
    }

    // Store the initial transcript when starting recording
    let initialTranscript = appendMode ? transcript : '';
    
    if (!appendMode) {
      setTranscript('');
      finalTranscriptRef.current = '';
      setCurrentSpeaker(1);
      initialTranscript = `[Speaker 1]: `;
      setTranscript(initialTranscript);
    } else {
      // In append mode, reset the ref but keep the current transcript
      finalTranscriptRef.current = '';
      // Add speaker marker if starting fresh or transcript is empty
      if (!initialTranscript || initialTranscript.trim() === '') {
        initialTranscript = `[Speaker 1]: `;
        setTranscript(initialTranscript);
        setCurrentSpeaker(1);
      }
    }

    try {
      const recognition = recognitionRef.current;
      
      // Set up event handlers
      recognition.onstart = () => {
        console.log('Speech recognition started');
        setStatus('Listening...');
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptText = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptText;
          } else {
            interimTranscript += transcriptText;
          }
        }

        if (finalTranscript) {
          // Check for voice commands to stop recording (only if entire phrase is "stop recording")
          const lowerTranscript = finalTranscript.toLowerCase().trim().replace(/[.,!?;:]+$/, '');
          
          // Check if the ENTIRE phrase is exactly "stop recording" and nothing else
          const isStopCommand = lowerTranscript === 'stop recording';
          
          if (isStopCommand) {
            // Don't add "stop recording" to the transcript at all
            console.log('Stop command detected, stopping recording...');
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
            return;
          }
          
          // Add space between phrases only if there's already content
          finalTranscriptRef.current += (finalTranscriptRef.current ? ' ' : '') + finalTranscript.trim();
          
          // Build the complete text with initial transcript if in append mode
          const baseText = appendMode && initialTranscript && initialTranscript.trim() 
            ? initialTranscript + ' ' 
            : appendMode ? initialTranscript : '';
          const newText = baseText + finalTranscriptRef.current;
          
          setTranscript(newText);
          
          // Auto-copy to clipboard (without adding to history during recording)
          navigator.clipboard.writeText(newText).catch(err => {
            console.error('Failed to copy to clipboard:', err);
          });
          
          setStatus('Text copied to clipboard');
        }

        // Show interim results
        if (interimTranscript) {
          const baseText = appendMode && initialTranscript && initialTranscript.trim() 
            ? initialTranscript + ' ' 
            : appendMode ? initialTranscript : '';
          const displayText = baseText + finalTranscriptRef.current + (finalTranscriptRef.current ? ' ' : '') + interimTranscript.trim();
          setTranscript(displayText);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        
        // Handle specific errors
        if (event.error === 'not-allowed') {
          setStatus('Microphone access denied. Please allow microphone access and try again.');
        } else if (event.error === 'no-speech') {
          setStatus('No speech detected. Please try again.');
        } else if (event.error === 'audio-capture') {
          setStatus('Audio capture failed. Please check your microphone.');
        } else {
          setStatus(`Error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsRecording(false);
        setStatus('Recording stopped');
        
        // Add final text to history when recording stops
        const baseText = appendMode && initialTranscript && initialTranscript.trim() 
          ? initialTranscript + ' ' 
          : appendMode ? initialTranscript : '';
        const finalText = baseText + finalTranscriptRef.current;
        if (finalText && finalText.trim()) {
          addToClipboardHistory(finalText);
        }
      };
      
      setStatus('Starting recording...');
      recognition.start();
      
    } catch (error) {
      console.error('Error starting recognition:', error);
      setStatus(`Error starting recording: ${error.message}`);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    console.log('Stop recording clicked');
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'stop_recording', {
        event_category: 'Speech Recognition',
        event_label: 'Stop recording button'
      });
    }
    
    if (recognitionRef.current && isRecording) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsRecording(false);
        setStatus('Recording stopped');
      }
    } else {
      setIsRecording(false);
      setStatus('Recording stopped');
    }
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    localStorage.setItem('selectedLanguage', newLanguage);
    
    // Reinitialize recognition if it's not recording
    if (!isRecording && recognitionRef.current) {
      recognitionRef.current.lang = newLanguage;
    }
    
    const langName = SUPPORTED_LANGUAGES.find(l => l.code === newLanguage)?.name || newLanguage;
    setStatus(`Language changed to ${langName}`);
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'change_language', {
        event_category: 'Settings',
        event_label: langName,
        value: newLanguage
      });
    }
  };

  const clearText = () => {
    console.log('Clear text clicked');
    setTranscript('');
    finalTranscriptRef.current = '';
    setCurrentSpeaker(1);
    setStatus('Text cleared');
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'clear_text', {
        event_category: 'Text Management',
        event_label: 'Clear text button'
      });
    }
  };

  const addNewSpeaker = () => {
    const nextSpeaker = currentSpeaker + 1;
    setCurrentSpeaker(nextSpeaker);
    
    const speakerMarker = `\n\n[Speaker ${nextSpeaker}]: `;
    const newTranscript = transcript + speakerMarker;
    setTranscript(newTranscript);
    
    // If recording, update the final transcript ref
    if (isRecording) {
      finalTranscriptRef.current = newTranscript;
    }
    
    setStatus(`Switched to Speaker ${nextSpeaker}`);
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_speaker', {
        event_category: 'Text Management',
        event_label: 'New speaker added',
        value: nextSpeaker
      });
    }
  };

  const handleTextChange = (e) => {
    setTranscript(e.target.value);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      addToClipboardHistory(transcript); // Add to history
      setStatus('Text copied to clipboard');
      
      // Google Analytics event
      if (typeof gtag !== 'undefined') {
        gtag('event', 'copy_text', {
          event_category: 'Text Management',
          event_label: 'Copy text to clipboard',
          value: transcript.length
        });
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setStatus('Failed to copy to clipboard');
    }
  };

  const toggleHistory = () => {
    const newShowHistory = !showHistory;
    setShowHistory(newShowHistory);
    
    // Scroll to history section when showing
    if (newShowHistory) {
      setTimeout(() => {
        historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'toggle_history', {
        event_category: 'History',
        event_label: newShowHistory ? 'Show history' : 'Hide history'
      });
    }
  };

  const closeHistory = () => {
    setShowHistory(false);
    
    // Google Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'close_history', {
        event_category: 'History',
        event_label: 'Close history panel'
      });
    }
  };

  console.log('About to render JSX...');

  return (
    <Container size="lg" padding='none' className="app">
      <Container size="lg" as="header" padding="md" style={{ position: 'relative' }}>
        <Heading level="h1" variant="display" centered style={{ marginBottom: '8px' }}>
          Speech2Text Tool
        </Heading>
        <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', zIndex: 1000 }}>
          <ThemeSwitcher variant="select" size="md" label='' />
        </div>
        <Heading level="h2" variant="subtitle" centered color="secondary">
          Convert your speech to text with ease
        </Heading>
      </Container>

      <Container size="lg" as="main" padding="none" style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}>
        
        <Card variant="elevated" padding="lg" style={{ borderRadius: '8px' }}>
          {isRecording && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', gap: '8px' }}>
              <StatusDot status="error" pulse size="sm" />
              <Text size="base" weight="medium" color="error">
                Recording...
              </Text>
            </div>
          )}

          <div style={{ marginBottom: '20px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label htmlFor="language-select" style={{ whiteSpace: 'nowrap' }}>
              <Text size="sm" weight="medium">
                🌐 Select Language
              </Text>
            </label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              disabled={isRecording}
              style={{
                flex: 1,
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid var(--input-border, #d1d5db)',
                borderRadius: '6px',
                backgroundColor: 'var(--input-bg, #ffffff)',
                color: 'var(--text-primary, #1f2937)',
                cursor: isRecording ? 'not-allowed' : 'pointer',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--input-border, #d1d5db)'}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              size="md"
              onClick={startRecording}
              disabled={isRecording}
            >
              Start Recording
            </Button>
            <Button
              variant="error"
              size="md"
              onClick={stopRecording}
              disabled={!isRecording}
            >
              Stop Recording
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={clearText}
              disabled={isRecording}
            >
              Clear Text
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={addNewSpeaker}
              disabled={!isRecording && !transcript}
            >
              👤 New Speaker
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={toggleHistory}
            >
              📋 History ({clipboardHistory.length})
            </Button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <Checkbox
                id="appendMode"
                size="md"
                checked={appendMode}
                onChange={(e) => {
                  setAppendMode(e.target.checked);
                  // Google Analytics event
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'toggle_append_mode', {
                      event_category: 'Settings',
                      event_label: e.target.checked ? 'Enable append mode' : 'Disable append mode'
                    });
                  }
                }}
              />
              <Text size="base" as="span">
                Append new recordings to existing text
              </Text>
            </label>
          </div>

          <Alert
            variant={
              status.includes('Error') || status.includes('Failed') ? 'error' : 
              status.includes('copied') || status.includes('Copied') ? 'success' : 
              'info'
            }
            size="medium"
            message={status}
            showIcon
            closable={false}
          />
        </Card>

        <Card variant="elevated" padding="none" style={{ borderRadius: '8px' }}>
          <Textarea
            value={transcript}
            onChange={handleTextChange}
            placeholder="Your speech will appear here... You can also type or edit the text manually."
            rows={10}
            size="md"
            autoResize
            minRows={5}
            maxRows={20}
            style={{ marginBottom: '10px' }}
          />
          <Button
            variant="primary"
            size="md"
            onClick={copyToClipboard}
            style={{ width: '100%' }}
          >
            Copy to Clipboard
          </Button>
        </Card>

        {showHistory && (
          <Card ref={historyRef} variant="elevated" padding="md" style={{ borderRadius: '8px', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid var(--card-border, #e9ecef)' }}>
              <Heading level="h3" variant="title">
                Clipboard History
              </Heading>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  variant="primary"
                  size="sm"
                  onClick={copyAllHistory}
                  disabled={clipboardHistory.length === 0}
                >
                  Copy All
                </Button>
                <Button 
                  variant="error"
                  size="sm"
                  onClick={clearHistory}
                  disabled={clipboardHistory.length === 0}
                >
                  Clear All
                </Button>
                <Button 
                  variant="secondary"
                  size="sm"
                  onClick={closeHistory}
                >
                  ✖
                </Button>
              </div>
            </div>
            
            {clipboardHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <Text size="base" color="secondary" as="p" style={{ fontStyle: 'italic' }}>
                  No clipboard history yet. Copy some text to see it here.
                </Text>
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {clipboardHistory.map((item) => (
                  <Card 
                    key={item.id}
                    variant="outlined"
                    padding="sm"
                    hoverable
                    style={{ transition: 'all 0.2s ease' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, marginRight: '12px' }}>
                        <Text size="base" as="div" weight="normal" style={{ wordBreak: 'break-word', marginBottom: '4px' }}>
                          {item.preview}
                        </Text>
                        <Text size="xs" color="secondary" as="div">
                          {new Date(item.timestamp).toLocaleString()}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => copyFromHistory(item.text)}
                        >
                          📋 Copy
                        </Button>
                        <Button
                          variant="error"
                          size="sm"
                          onClick={() => deleteHistoryItem(item.id)}
                        >
                          🗑
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card variant="subtle" padding="none" style={{ marginTop: '30px', borderRadius: '6px' }}>
          <Text size="sm" color="secondary" align="center" as="p">
            © 2025 Developed by{' '}
            <Link 
              href="https://hlab.au/" 
              variant="accent" 
              external
            >
              HLab.au
            </Link>
            {' • '}
            Powered by{' '}
            <Link 
              href="https://www.atomiccomponents.com" 
              variant="accent" 
              external
            >
              Atomic Components
            </Link>
          </Text>
        </Card>
      </Container>
    </Container>
  );
}

export default App;
